import { z } from "zod";
import type { PromoCodeMetricsRow } from "@/core/components/sites/shared/promo-codes/promoCodes";

// Re-exported so this module stays the single import point for the metrics
// response, while the row shape stays shared with the group report.
export type { PromoCodeMetricsRow };

export enum Origin {
	All = "All",
	Web = "Web",
	Mews = "Mews",
}
export const metricsSearchSchema = z.object({
	startDate: z.string(),
	endDate: z.string(),
});

export interface AveragePriceAndDistribution {
	totalBooking: number;
	averageBookingPrice: number;
	distribution: Distribution[];
}

export interface Distribution {
	category: string;
	revenue: number;
	bookings: number;
	percentage: string;
}
export interface BookingsByCountry {
	country: string;
	totalBookings: number;
	revenue: number;
}

export interface BookingsGroupByType {
	currentTotals: {
		totalBookings: number;
		rates: number;
		activities: number;
		packages: number;
		activitiesRevenue: number;
		ratesRevenue: number;
		packagesRevenue: number;
		totalRevenue: number;
	};
	trendPercentages: {
		totalBookings: number;
		rates: number;
		activities: number;
		packages: number;
	};
	data: BookingGroupTypeData[];
}

export interface BookingGroupTypeData {
	date: string;
	totalBookings: BookingGroupType;
	rates: BookingGroupType;
	activities: BookingGroupType;
	packages: BookingGroupType;
}

export interface BookingGroupType {
	current: number;
	previous: number;
	difference: number;
	percentage: number;
}

export interface CompletedStays {
	currentTotals: {
		completedBookings: number;
		revenue: number;
	};
	trendPercentages: {
		completedBookings: number;
		revenue: number;
	};
	data: CompletedSaysType[];
}

export interface CompletedSaysType {
	date: string;
	completedBookings: BookingGroupType;
}

export interface OccupancyTrend {
	date: string;
	occupancyPercentage: BookingGroupType;
	roomsBooked: BookingGroupType;
}

export interface OccupancyByMonth {
	currentTotals: {
		occupancyPercentage: number;
		roomsBooked: number;
	};
	trendPercentages: {
		occupancyPercentage: number;
		roomsBooked: number;
	};
	data: OccupancyTrend[];
}

export type DeviceMetrics = {
	activeUsers: number;
	sessions: number;
	transactions: number;
};

export type AnalyticsDateEntry = {
	date: string;
	devices: Record<string, DeviceMetrics>;
};

export type Analytics = {
	totalPurchase: number;
	totalSession: number;
	totalActiveUser: number;
	conversionRate: number;
	totalsByDevice: Record<string, DeviceMetrics>;
	dates: AnalyticsDateEntry[];
};

// One row per room block: eventName and/or groupCode depending on what the
// block had at booking time. Distinct concepts — never substitute one for
// the other (nor for the block name) when rendering. Promo code attribution
// lives in PromoCodeMetricsRow (agent-aware); the sections are NOT mutually
// exclusive — a booking can carry a promo code AND come from a room block.
export interface RoomBlockMetricItem {
	roomBlockId: string;
	eventName: string | null;
	groupCode: string | null;
	uses: number;
	revenue: number;
}

export interface CodesMetricsSection<T> {
	totalUses: number;
	totalRevenue: number;
	items: T[];
}

export interface Metrics {
	analytics: Analytics;
	averagePriceAndDistribution: AveragePriceAndDistribution;
	bookingsByCountry: BookingsByCountry[];
	bookingsGroupByType: BookingsGroupByType;
	cancellationsGroupByType: BookingsGroupByType;
	completedStays: CompletedStays;
	occupancyByMonth: OccupancyByMonth;
	promoCodes: PromoCodeMetricsRow[];
	roomBlocks: CodesMetricsSection<RoomBlockMetricItem>;
}
